import { gql } from "@apollo/client";

/**
 * QUERIES
 * */

export const SPECIAL_PACK_PRODUCT_FRAGMENT = gql`
  fragment SpecialPackProduct on ProductObject {
    id
    name
    slug
    skuCode
    sellingPrice
    coverPhotoMedium
    hasStock
    specialPackEditionSlug
    specialPackEditionListingDescription
  }
`;

export const GET_ALL_NON_WINE_SPECIAL_PACK_PRODUCTS = gql`
  query AllNonWineSpecialPackProducts($limit: Int = 20, $offset: Int = 0, $availableOnly: Boolean = true) {
    allNonWineSpecialPackProducts(limit: $limit, offset: $offset, availableOnly: $availableOnly) {
      ...SpecialPackProduct
    }
    allNonWineSpecialPackProductsCount(availableOnly: $availableOnly)
  }
  ${SPECIAL_PACK_PRODUCT_FRAGMENT}
`;

export const GET_SPECIAL_PACK_COLLECTIONS = gql`
  query SpecialPackCollections($limit: Int = 5, $offset: Int = 0, $productLimit: Int = 20) {
    collections(page: SPECIAL_PACKS, excludeEmptyCollections: true, limit: $limit, offset: $offset) {
      id
      name
      slug
      description
      productCount
      products(limit: $productLimit, offset: 0) {
        ...SpecialPackProduct
      }
    }
    collectionsCount(page: SPECIAL_PACKS, excludeEmptyCollections: true)
  }
  ${SPECIAL_PACK_PRODUCT_FRAGMENT}
`;

export const GET_SPECIAL_PACK_COLLECTION = gql`
  query SpecialPackCollection($slug: String!, $limit: Int = 20, $offset: Int = 0) {
    collectionBySlug(slug: $slug) {
      id
      name
      slug
      description
      productCount
      products(limit: $limit, offset: $offset) {
        ...SpecialPackProduct
      }
    }
  }
  ${SPECIAL_PACK_PRODUCT_FRAGMENT}
`;

export const GET_ALL_SPECIAL_PACK_EDITIONS = gql`
  query AllSpecialPackEditions($limit: Int, $offset: Int, $availableOnly: Boolean) {
    allSpecialPackEditions(limit: $limit, offset: $offset, availableOnly: $availableOnly) {
      id
      name
      listingDescription
      listingImageUrl
      slug
      specialpackoptionSet {
        id
        displayName
        product {
          id
          name
          isEnabled
          sellingPrice
          coverPhotoSmall
          slug
          productType {
            id
          }
        }
      }
    }
  }
`;

export const SPECIAL_PACK_EDITION_DETAILS = gql`
  fragment SpecialPackEditionDetails on SpecialPackEditionObject {
    id
    name
    slug
    endDate
    heroTopImageLargeUrl
    listingImageUrl
    buyButtonText
    heroTopTitle
    heroTopSubTitle
    heroTopTheme
    heroTopTextPosition
    heroDisabledText
    section1Title
    section1Text
    section2Title
    section2Text
    section3Title
    section3Text
    heroBottomTitle
    heroBottomSubTitle
    heroBottomTheme
    seoTitle
    seoDescription
    heroBottomImageLargeUrl
    section1ImageLargeUrl
    section2ImageLargeUrl
    section3ImageLargeUrl
    isAvailable
    specialPackType {
      id
    }
    specialpackoptionSet {
      id
      displayName
      sortOrder
      product {
        id
        name
        isEnabled
        productType {
          id
          isEnabled
          name
        }
        coverPhotoLarge
        slug
        skuCode
        sellingPrice
        productPhotos {
          id
          photoLarge
          photoSmall
        }
        wine {
          id
          wineMaker {
            name
            id
          }
          product {
            name
            id
            productType {
              id
              name
            }
            productPhotos {
              id
              photoLarge
            }
            sellingPrice
          }
          wineType {
            name
            id
          }
          wineRegion {
            name
            id
          }
          country {
            name
            id
            code
          }
          wineBody {
            name
            id
          }
          wineTannin {
            name
            id
          }
          wineAcidity {
            name
            id
          }
          wineSweetness {
            name
            id
          }
          wineBarrelType {
            name
            id
          }
          wineCellarPeriod {
            name
            id
          }
          wineProductionMethods {
            name
            id
          }
          fullDescription
          country {
            id
            name
            code
          }
          food {
            name
            id
          }
          moods {
            name
            id
          }
          tastes {
            name
            id
          }
          year
          alcoholPercentage
          sommelierNotes
          pairingsDescription
          oakAged
          memberLikelihood
        }
      }
    }
  }
`;

export const GET_SPECIAL_PACK_EDITION_DETAILS = gql`
  ${SPECIAL_PACK_EDITION_DETAILS}
  query SpecialPackEdition($slug: String!) {
    specialPackEdition(slug: $slug) {
      ...SpecialPackEditionDetails
    }
  }
`;

export const GET_SPECIAL_PACK_EDITION_OPTIONS = gql`
  query SpecialPackEditionOptions($slug: String!) {
    specialPackEdition(slug: $slug) {
      id
      name
      slug
      specialpackoptionSet {
        id
        displayName
        sortOrder
        product {
          id
          name
          isEnabled
          sellingPrice
          coverPhotoSmall
          slug
          productType {
            id
          }
        }
      }
    }
  }
`;

// The product sub-selection is the exact set AddProductToShoppingCartButton
// denormalises into the guest local cart (plus the cart tray thumbnail), and
// deliberately NOT the SpecialPackEditionDetails fragment — the landing page
// must not drag in the full wine graph of every option.
// buildYourOwnPromoCode is null when "Build your own" is off for the edition;
// its freebie rows drive the option's copy (box name/image, wines per box).
export const GET_ACTIVE_ADVENT_CALENDAR = gql`
  query ActiveAdventCalendar {
    getActiveAdventCalendar {
      id
      name
      slug
      endDate
      despatchStartDate
      buildYourOwnPromoCode {
        id
        name
        promocodefreebieSet {
          id
          quantity
          perNumberOfWines
          perWineSpend
          requiresCompleteUnits
          freebie {
            id
            imageMedium
            product {
              id
              name
            }
          }
        }
      }
      specialpackoptionSet {
        id
        displayName
        sortOrder
        pricePoint
        wineStyle
        product {
          id
          name
          slug
          isEnabled
          sellingPrice
          coverPhotoSmall
          productType {
            id
          }
        }
      }
    }
  }
`;

/**
 * MUTATIONS
 * */
export const ADD_SPECIAL_PACK_INTEREST = gql`
  mutation RegisterSpecialPackInterest($input: RegisterSpecialPackInterestInput!) {
    registerSpecialPackInterest(input: $input) {
      isSuccessful
      errors {
        field
        messages
      }
    }
  }
`;
